import { Link } from "react-router-dom";
import { YOUTUBE_CHANNEL_URL } from "@/data/youtubeVideos";
import { Eyebrow } from "@/components/layout/Eyebrow";

interface FooterColumn {
  heading: string;
  links: Array<{ label: string; to?: string; href?: string }>;
}

const columns: FooterColumn[] = [
  {
    heading: "Community",
    links: [
      { label: "Feed", to: "/feed" },
      { label: "News", to: "/news" },
      { label: "Trip Reports", to: "/trip-reports" },
      { label: "Disney Directory", to: "/disney" },
      { label: "Tips", to: "/tips" },
      { label: "Reviews", to: "/reviews" },
      { label: "Checklists", to: "/checklists/public" },
      { label: "Events", to: "/events" },
      { label: "Videos", to: "/videos" },
      { label: "Creators", to: "/creators" },
      { label: "Bookmarks", to: "/bookmarks" },
      { label: "Become a Creator", to: "/creator/apply" },
    ],
  },
  {
    heading: "Disney Guides",
    links: [
      { label: "Disney World Tips", to: "/disney-world-tips" },
      { label: "Photography", to: "/disney-photography" },
      { label: "Trip Planning", to: "/disney-trip-planning" },
      { label: "Attractions", to: "/disney-attractions" },
      { label: "Restaurants", to: "/disney-restaurants" },
      { label: "Reviews", to: "/disney-world-reviews" },
      { label: "Checklists", to: "/disney-checklists" },
    ],
  },
  {
    heading: "Creators",
    links: [
      { label: "Influencers", to: "/influencers" },
      { label: "Photographers", to: "/photographers" },
      { label: "Vloggers", to: "/vloggers" },
      { label: "Trip Planners", to: "/trip-planners" },
      { label: "Disney Travel Agencies", to: "/travel-agents" },
      { label: "Become a Travel Agent", to: "/travel-agents/apply" },
      { label: "YouTube", href: YOUTUBE_CHANNEL_URL },
    ],
  },
  {
    heading: "About",
    links: [
      { label: "About Us", to: "/about" },
      { label: "FAQ", to: "/faq" },
      { label: "Support", to: "/support" },
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
    ],
  },
];

export const Footer = () => {
  return (
    <footer className="mt-auto bg-footer text-footer-foreground border-t-2 border-secondary">
      <div className="container mx-auto px-6 md:px-12 py-16 md:py-20">
        {/* Masthead */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-12 border-b border-footer-border">
          <div className="lg:col-span-5 space-y-6">
            <Eyebrow tone="inverse">Tomorrow33 — Vol. 01</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-[0.95]">
              An independent Disney
              <br />
              fan <span className="text-accent">publication</span>.
            </h2>
            <p className="max-w-md text-sm text-footer-muted leading-relaxed">
              Real tips, honest reviews, photographer spotlights, and trip-planning checklists from people who actually live and breathe the parks.
            </p>
          </div>

          {/* Columns */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-8">
            {columns.map((col) => (
              <div key={col.heading} className="space-y-4">
                <div className="space-y-2">
                  <h3 className="font-display text-xs font-bold uppercase tracking-widest text-footer-foreground">
                    {col.heading}
                  </h3>
                  <div className="h-px w-8 bg-secondary" aria-hidden />
                </div>
                <ul className="space-y-2">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      {link.href ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-footer-muted hover:text-accent transition-colors"
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link
                          to={link.to!}
                          className="text-sm text-footer-muted hover:text-accent transition-colors"
                        >
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Colophon */}
        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-[10px] uppercase tracking-widest text-footer-muted">
            © {new Date().getFullYear()} Tomorrow33 — An unofficial fan publication
          </p>
          <p className="text-[10px] uppercase tracking-widest text-footer-muted/70 max-w-xl md:text-right">
            Not affiliated with, sponsored by, or endorsed by The Walt Disney Company. All Disney marks belong to their respective owners.
          </p>
        </div>
      </div>
    </footer>
  );
};
